import React from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { languages, type LanguageCode } from '../i18n/languages';

interface HeaderProps {
  unitSystem: 'metric' | 'imperial';
  setUnitSystem: (unit: 'metric' | 'imperial') => void;
  activeView: string;
  setActiveView: (view: string) => void;
  onNavigate?: (path: string, view: string, tab: string) => void;
  onPrint: () => void;
  onShowToast: (msg: string) => void;
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  unitSystem,
  setUnitSystem,
  activeView,
  setActiveView,
  onNavigate,
  onPrint,
  onShowToast,
  isDarkMode,
  onToggleTheme,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const { language, setLanguage, t } = useLanguage();

  const handleNav = (path: string, view: string, tab: string = 'water') => {
    if (onNavigate) {
      onNavigate(path, view, tab);
    } else {
      setActiveView(view);
    }
  };

  const handleLanguageChange = (code: string) => {
    const selectedLanguage = languages.find(
      (item) => item.code === code
    );

    if (!selectedLanguage) return;

    setLanguage(selectedLanguage.code as LanguageCode);
    onShowToast(`Language changed to ${selectedLanguage.nativeName}`);
  };

  const navigationClass = (view: string) => {
    return `px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
      activeView === view
        ? 'text-[#087F9C] bg-teal-50 dark:bg-teal-900/30'
        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
    }`;
  };

  return (
    <>
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-[#10283D] via-[#087F9C] to-[#10283D] text-white text-[11px] sm:text-xs py-1.5 px-3 sm:px-4 text-center font-medium shadow-sm flex items-center justify-center gap-1.5 sm:gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
        <span className="leading-tight">
          <strong>PipeSizingCalculator.com v2.4</strong>
          <span className="hidden sm:inline"> — Engineering Hydraulic Suite • Zero API Latency</span>
          <span className="sm:hidden"> — Hydraulic Suite</span>
        </span>
      </div>

      {/* Main Header */}
      <header className="bg-white dark:bg-[#1F2937] border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40 shadow-xs">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">

          <div className="flex items-center justify-between h-14 sm:h-16 gap-2">

            {/* Logo */}
            <div
              className="flex items-center gap-2.5 sm:gap-3 cursor-pointer shrink-0"
              onClick={() => handleNav('/', 'calculators', 'water')}
            >
              <img
                src="/assets/logo.png"
                alt="Pipe Sizing Calculator"
                width={40}
                height={40}
                className="w-8 h-8 sm:w-10 sm:h-10 object-contain rounded-xl shrink-0"
              />

              <div>
                <span className="font-display font-extrabold text-base sm:text-xl text-[#10283D] dark:text-white tracking-tight">
                  PipeSizing
                  <span className="text-[#087F9C]">Calculator</span>
                </span>

                <span className="hidden sm:block text-[10px] uppercase font-bold tracking-wider text-slate-400">
                  Engineering Hydraulic Suite
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              <button
                onClick={() => handleNav('/', 'calculators', 'water')}
                className={navigationClass('calculators')}
              >
                {t('calculators')}
              </button>

              <button
                onClick={() => handleNav('/pipe-size-charts/', 'tables', 'water')}
                className={navigationClass('tables')}
              >
                {t('pipeSizeCharts')}
              </button>

              <button
                onClick={() => handleNav('/guides/', 'guides', 'water')}
                className={navigationClass('guides')}
              >
                {t('guides')}
              </button>

              <button
                onClick={() => handleNav('/blog/', 'blog-home', 'water')}
                className={navigationClass('blog-home')}
              >
                Blog
              </button>

              <button
                onClick={() => handleNav('/about-us/', 'about', 'water')}
                className={navigationClass('about')}
              >
                {t('about')}
              </button>
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-1.5 sm:gap-2">

              {/* Language Selector (Desktop / Tablet >= 640px) */}
              <select
                value={language}
                onChange={(event) =>
                  handleLanguageChange(event.target.value)
                }
                aria-label={t('selectLanguage')}
                className="hidden sm:block max-w-[125px] bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-white border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2 text-xs font-semibold outline-none"
              >
                {languages.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.nativeName}
                  </option>
                ))}
              </select>

              {/* Unit Selector (Desktop / Tablet >= 640px) */}
              <div className="hidden sm:flex items-center bg-slate-100 dark:bg-slate-700 p-1 rounded-xl border border-slate-200 dark:border-slate-600 text-xs font-semibold">
                <button
                  onClick={() => {
                    setUnitSystem('metric');
                    onShowToast('Switched to Metric (SI) Units');
                  }}
                  className={`px-2 py-1 rounded-lg ${
                    unitSystem === 'metric'
                      ? 'bg-[#087F9C] text-white'
                      : 'text-slate-600 dark:text-slate-200'
                  }`}
                >
                  Metric
                </button>

                <button
                  onClick={() => {
                    setUnitSystem('imperial');
                    onShowToast('Switched to Imperial Units');
                  }}
                  className={`px-2 py-1 rounded-lg ${
                    unitSystem === 'imperial'
                      ? 'bg-[#087F9C] text-white'
                      : 'text-slate-600 dark:text-slate-200'
                  }`}
                >
                  Imperial
                </button>
              </div>

              {/* Dark Mode Toggle */}
              <button
                type="button"
                onClick={onToggleTheme}
                aria-label={
                  isDarkMode ? t('lightMode') : t('darkMode')
                }
                title={
                  isDarkMode ? t('lightMode') : t('darkMode')
                }
                className={`inline-flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full border shadow-sm transition-all duration-300 hover:scale-105 shrink-0 ${
                  isDarkMode
                    ? 'bg-[#087F9C] border-[#39BFD3]/50 text-white'
                    : 'bg-[#10283D] border-[#334B60] text-[#FACC15]'
                }`}
              >
                {isDarkMode ? (
                  <svg
                    className="w-5 h-5 sm:w-[23px] sm:h-[23px]"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M20.2 15.1A8.7 8.7 0 0 1 8.9 3.8a8.8 8.8 0 1 0 11.3 11.3Z" />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 sm:w-[23px] sm:h-[23px]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="4"
                      fill="currentColor"
                      stroke="none"
                    />
                    <path d="M12 2v2.2M12 19.8V22M4.93 4.93l1.55 1.55m11.04 11.04l1.55 1.55M2 12h2.2m15.6 0H22M4.93 19.07l1.55-1.55M17.52 6.48l1.55-1.55" />
                  </svg>
                )}
              </button>

              {/* PDF Report (Desktop / Tablet >= 640px) */}
              <button
                onClick={onPrint}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#10283D] text-white hover:bg-slate-800"
              >
                PDF Report
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-1.5 sm:p-2 rounded-lg text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 shrink-0"
                aria-label="Toggle navigation menu"
                aria-expanded={mobileMenuOpen}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Utility Control Strip (< 640px) */}
          <div className="sm:hidden border-t border-slate-200/80 dark:border-slate-700/80 py-1.5 flex items-center justify-between gap-2">
            {/* Mobile Language Selector */}
            <div className="flex items-center gap-1.5 min-w-0">
              <svg
                className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                />
              </svg>
              <select
                value={language}
                onChange={(event) =>
                  handleLanguageChange(event.target.value)
                }
                aria-label={t('selectLanguage')}
                className="max-w-[130px] bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-white border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 text-xs font-semibold outline-none"
              >
                {languages.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.nativeName}
                  </option>
                ))}
              </select>
            </div>

            {/* Mobile Unit Selector */}
            <div className="flex items-center bg-slate-100 dark:bg-slate-700 p-0.5 rounded-lg border border-slate-200 dark:border-slate-600 text-[11px] font-semibold shrink-0">
              <button
                onClick={() => {
                  setUnitSystem('metric');
                  onShowToast('Switched to Metric (SI) Units');
                }}
                className={`px-2 py-0.5 rounded-md transition-colors ${
                  unitSystem === 'metric'
                    ? 'bg-[#087F9C] text-white shadow-xs'
                    : 'text-slate-600 dark:text-slate-200'
                }`}
              >
                Metric
              </button>

              <button
                onClick={() => {
                  setUnitSystem('imperial');
                  onShowToast('Switched to Imperial Units');
                }}
                className={`px-2 py-0.5 rounded-md transition-colors ${
                  unitSystem === 'imperial'
                    ? 'bg-[#087F9C] text-white shadow-xs'
                    : 'text-slate-600 dark:text-slate-200'
                }`}
              >
                Imperial
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-slate-200 dark:border-slate-700 py-3 space-y-2">

              <div className="px-3">
                <select
                  value={language}
                  onChange={(event) =>
                    handleLanguageChange(event.target.value)
                  }
                  aria-label={t('selectLanguage')}
                  className="w-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-white border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm"
                >
                  {languages.map((item) => (
                    <option key={item.code} value={item.code}>
                      {item.nativeName}
                    </option>
                  ))}
                </select>
              </div>

              <button
                onClick={() => {
                  handleNav('/', 'calculators', 'water');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 rounded-md text-sm font-semibold text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {t('calculators')}
              </button>

              <button
                onClick={() => {
                  handleNav('/pipe-size-charts/', 'tables', 'water');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {t('pipeSizeCharts')}
              </button>

              <button
                onClick={() => {
                  handleNav('/guides/', 'guides', 'water');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {t('guides')}
              </button>

              <button
                onClick={() => {
                  handleNav('/blog/', 'blog-home', 'water');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Engineering Blog
              </button>

              <button
                onClick={() => {
                  handleNav('/about-us/', 'about', 'water');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                {t('about')}
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
};